@let tabbed = hasEngineWorkflow();
@let current = activeView();

<nav
  #workflowHeader
  [attr.role]="tabbed ? 'tablist' : null"
  class="workflow-header"
  aria-label="Workflow header">
  @if (!isProductionDeviation()) {
  <button
    type="button"
    class="workflow-header__item"
    [attr.role]="tabbed ? 'tab' : null"
    [class.workflow-header__item--active]="current === 'summary'"
    [attr.aria-selected]="tabbed ? current === 'summary' : null"
    [attr.aria-current]="!tabbed && current === 'summary' ? 'location' : null"
    (click)="scrollToView('summary')">
    <span>summary</span>
  </button>
  }
  @if (tabbed && !isProductionDeviation()) {
    <button
      type="button"
      class="workflow-header__item"
      role="tab"
      [class.workflow-header__item--active]="current === 'engine'"
      [attr.aria-selected]="current === 'engine'"
      (click)="scrollToView('engine')">
      <span>workflow</span>
      @if (workflowTabBadge(); as badge) {
        <span class="visually-hidden">, </span>
        <span class="workflow-header__badge">{{ badge }}</span>
      }
    </button>
  }
  @if (!isProductionDeviation()) {
  <button
    type="button"
    class="workflow-header__item"
    [attr.role]="tabbed ? 'tab' : null"
    [class.workflow-header__item--active]="current === 'recommended'"
    [attr.aria-selected]="tabbed ? current === 'recommended' : null"
    [attr.aria-current]="!tabbed && current === 'recommended' ? 'location' : null"
    (click)="scrollToView('recommended')">
    <span>recommended actions</span>
  </button>
  }
  <button
    type="button"
    class="workflow-header__item"
    [attr.role]="tabbed ? 'tab' : null"
    [class.workflow-header__item--active]="current === 'hypothesis'"
    [attr.aria-selected]="tabbed ? current === 'hypothesis' : null"
    [attr.aria-current]="!tabbed && current === 'hypothesis' ? 'location' : null"
    (click)="scrollToView('hypothesis')">
    <span>{{ candidateSectionLabel() }}</span>
  </button>
  @if (tabbed && isProductionDeviation()) {
    <button
      type="button"
      class="workflow-header__item"
      role="tab"
      [class.workflow-header__item--active]="current === 'engine'"
      [attr.aria-selected]="current === 'engine'"
      (click)="scrollToView('engine')">
      <span>workflow</span>
      @if (workflowTabBadge(); as badge) {
        <span class="visually-hidden">, </span>
        <span class="workflow-header__badge">{{ badge }}</span>
      }
    </button>
  }
  <button
    type="button"
    class="workflow-header__item"
    [attr.role]="tabbed ? 'tab' : null"
    [class.workflow-header__item--active]="current === 'comments'"
    [attr.aria-selected]="tabbed ? current === 'comments' : null"
    [attr.aria-current]="!tabbed && current === 'comments' ? 'location' : null"
    (click)="scrollToView('comments')">
    <span>comments</span>
  </button>
  @if (hasInvestigationSection()) {
    <button
      type="button"
      class="workflow-header__item"
      [attr.role]="tabbed ? 'tab' : null"
      [class.workflow-header__item--active]="current === 'workflow'"
      [attr.aria-selected]="tabbed ? current === 'workflow' : null"
      [attr.aria-current]="!tabbed && current === 'workflow' ? 'location' : null"
      (click)="scrollToView('workflow')">
      <span>investigation</span>
    </button>
  }
</nav>

@if (!isProductionDeviation() && (!tabbed || current === 'summary')) {
<section #summarySection [attr.role]="tabbed ? 'tabpanel' : null" [class.detail-scroll-section--card]="tabbed" class="detail-scroll-section summary-panel">
  <div class="summary-panel__header">
    <h3 class="summary-panel__title">summary</h3>
    <ng-content select="[summaryFeedback]"></ng-content>
  </div>
  @if (summaryText().trim().length > 0) {
    <p class="summary-panel__text">{{ summaryText() }}</p>
  } @else {
    <p class="summary-panel__text summary-panel__text--empty">No summary available.</p>
  }
</section>
}

<ng-template #evidenceRecord let-evidenceView>
  <details class="uc1-evidence" [attr.data-evidence-id]="evidenceView.id">
    <summary>
      <i class="pi pi-chevron-right disclosure-chevron" aria-hidden="true"></i>
      <span>{{ evidenceView.record?.title ?? evidenceView.id }}</span>
      <span class="uc1-evidence__meta">{{ evidenceView.displayLabel }}</span>
    </summary>
    @if (evidenceView.record?.source) {
      <p class="uc1-evidence__source">Source: {{ evidenceView.record?.source }}</p>
    }
    @if (evidenceView.blocks.length) {
      <app-detail-summary-blocks [blocks]="evidenceView.blocks" [wrapTables]="true" />
    }
    @if (evidenceView.issue) {
      <p class="uc1-evidence__issue" role="status">{{ evidenceView.issue }}</p>
    }
    <p class="uc1-evidence__id">Evidence ID: {{ evidenceView.id }}</p>
  </details>
</ng-template>

@if (tabbed && current === 'engine') {
  <section #engineSection role="tabpanel" class="detail-scroll-section">
    <ng-content select="[engineWorkflow]"></ng-content>
  </section>
}

@if (!isProductionDeviation() && (!tabbed || current === 'recommended')) {
<section #recommendedSection [attr.role]="tabbed ? 'tabpanel' : null" [class.detail-scroll-section--card]="tabbed" class="detail-scroll-section recommended-panel">
  <h3 class="recommended-panel__title">recommended actions</h3>
  <ng-container [ngTemplateOutlet]="recommendedContent" />
</section>
}

<ng-template #recommendedContent>
  <div class="recommended-panel__callout">
    <p class="recommended-panel__callout-title">
      <span class="recommended-panel__callout-icon" aria-hidden="true">✦</span>
      <span>AI AGENT — RECOMMENDED CORRECTIVE ACTION</span>
    </p>

    @if (recommendedActions().length > 0) {
      <ul class="recommended-panel__list">
        @for (action of recommendedActions(); track action) {
          <li>{{ action }}</li>
        }
      </ul>
    } @else {
      <p class="recommended-panel__empty">No proposed actions.</p>
    }
  </div>

  @if (!hasDecision()) {
    <div class="recommended-panel__actions">
      <button type="button" class="iopoc-button small secondary" (click)="reject.emit()">reject and escalate</button>
      <button type="button" class="iopoc-button small primary" (click)="approve.emit()">approve</button>
    </div>
  }
</ng-template>

@if (!tabbed || current === 'hypothesis') {
<section #hypothesisSection [attr.role]="tabbed ? 'tabpanel' : null" [class.detail-scroll-section--card]="tabbed" class="detail-scroll-section hypothesis-panel">
  <h3 class="hypothesis-panel__title">{{ candidateSectionLabel() }}</h3>
  @if (hasUc1Candidates()) {
    @if (sortedUc1Candidates().length) {
      <div class="uc1-candidate-stack">
        @for (candidateView of uc1CandidateViews(); track candidateView.candidate.candidateId) {
          @let candidate = candidateView.candidate;
          <details [open]="$first" class="hyp-card uc1-candidate">
            <summary class="hyp-card__head">
              <i class="pi pi-chevron-right disclosure-chevron" aria-hidden="true"></i>
              <span class="hyp-card__rank">{{ candidateView.displayRank }}</span>
              @if (candidate.assessment) {
                <span class="uc1-candidate__assessment">{{ candidate.assessment }}</span>
              }
              <h4 class="hyp-card__title">{{ candidate.exception?.title ?? candidate.candidateId }}</h4>
            </summary>
            @if (candidate.exception?.mechanism) {
              <p class="hyp-card__body">{{ candidate.exception?.mechanism }}</p>
            }
            <dl class="uc1-candidate__facts">
              @if (candidate.exception?.assetCodes?.length) {
                <div>
                  <dt>Assets</dt>
                  <dd>{{ candidate.exception?.assetCodes?.join(', ') }}</dd>
                </div>
              }
              @if (candidate.exception?.affectedStreams?.length) {
                <div>
                  <dt>Affected streams</dt>
                  <dd>{{ candidate.exception?.affectedStreams?.join(', ') }}</dd>
                </div>
              }
            </dl>
            @if (candidate.recommendedAction) {
              <div class="uc1-candidate__detail">
                <h5>Recommended action</h5>
                <p>{{ candidate.recommendedAction }}</p>
              </div>
            }
            @if (candidate.evidenceGaps?.length) {
              <div class="uc1-candidate__detail">
                <h5>Evidence gaps</h5>
                <ul>
                  @for (gap of candidate.evidenceGaps; track $index) {
                    <li>{{ gap }}</li>
                  }
                </ul>
              </div>
            }
            @if (candidateView.evidence.length) {
              <div class="uc1-candidate__detail">
                <h5>Supporting evidence ({{ candidateView.evidence.length }})</h5>
                <div class="uc1-evidence-list">
                  @for (evidenceView of candidateView.evidence; track evidenceView.id) {
                    <ng-container [ngTemplateOutlet]="evidenceRecord" [ngTemplateOutletContext]="{ $implicit: evidenceView }" />
                  }
                </div>
              </div>
            }
          </details>
        }
      </div>
    } @else {
      <p class="hypothesis-panel__empty">No candidates available.</p>
    }
  } @else if (hypotheses().length) {
    <div class="hyp-stack">
      @for (hyp of hypotheses(); track $index) {
        <div class="hyp-card hyp-card--{{ hyp.tag }}">
          <div class="hyp-card__head">
            <span class="hyp-card__rank">{{ $index + 1 }}</span>
            <span class="hyp-card__tag hyp-card__tag--{{ hyp.tag }}">{{ hyp.tag }}</span>
            <h4 class="hyp-card__title">{{ hyp.title }}</h4>
          </div>
          <p class="hyp-card__body" [innerHTML]="safeHtml(hyp.body)"></p>
        </div>
      }
    </div>
  } @else {
    <p class="hypothesis-panel__empty">No hypotheses available.</p>
  }
</section>
}

@if (!tabbed || current === 'comments') {
<section #commentsSection [attr.role]="tabbed ? 'tabpanel' : null" [class.detail-scroll-section--card]="tabbed" class="detail-scroll-section comments-panel">
  <h3 class="comments-panel__title">comments</h3>
  <div class="comments-panel__comments">
    <app-detail-comments
      [comments]="comments()"
      [open]="commentsOpen()"
      [draft]="commentDraft()"
      [posting]="commentPosting()"
      (toggled)="toggleComments.emit()"
      (draftChanged)="commentDraftChanged.emit($event)"
      (posted)="commentPosted.emit()" />
  </div>
</section>
}

@if (hasInvestigationSection() && (!tabbed || current === 'workflow')) {
<section #workflowSection [attr.role]="tabbed ? 'tabpanel' : null" [class.detail-scroll-section--card]="tabbed" class="detail-scroll-section">
  <div class="steps-label">investigation</div>
  <div class="steps-scroll">
    <div class="steps">
      @for (step of visibleSteps(); track step.number; let isLast = $last) {
        <div class="step" [class.step--open]="isStepOpen(step.number)">
          <div [class]="getStepDotStatusClass(step.status)" aria-hidden="true">
            @if (step.status === StepStatus.Done) {
              <span>✓</span>
            } @else {
              <span>{{ step.number }}</span>
            }
          </div>
          @if (!isLast) {
            <div class="step-line"></div>
          }
          <div class="step-info">
            <button
              type="button"
              class="step-header"
              [id]="'workflow-step-trigger-' + step.number"
              [attr.aria-expanded]="isStepOpen(step.number)"
              [attr.aria-controls]="'workflow-step-panel-' + step.number"
              (click)="toggleStep(step.number)">
              <span class="step-title-text">{{ getStepTitle(step) }}</span>
              <span [class]="getActorClass(step.actor)">{{ getActorLabel(step.actor) }}</span>
              @if (step.action) {
                <span class="decision-pill decision-pill--{{ step.action.toLowerCase() }}">
                  {{ step.action === 'Approve' ? 'Approved' : 'Rejected' }}
                </span>
              }
              <span class="step-toggle-icon" [class.step-toggle-icon--open]="isStepOpen(step.number)" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
            @if (isStepOpen(step.number) && (step.completedOn || step.source || step.completedBy)) {
              <div class="step-inline-meta-stack">
                @if (step.completedOn || step.source) {
                  <div class="step-inline-meta">
                    @if (step.completedOn) {
                      <span class="step-inline-meta__k">Date</span>
                      <span class="step-inline-meta__v">{{ step.completedOn | date: 'MMM d, yyyy, HH:mm:ss' : '+0100' }} WAT</span>
                    }
                    @if (step.completedOn && step.source) {
                      <span class="step-inline-meta__sep">·</span>
                    }
                    @if (step.source) {
                      <span class="step-inline-meta__k">Source</span>
                      <span class="step-inline-meta__v">{{ step.source }}</span>
                    }
                  </div>
                }

                @if (step.completedBy || step.completedOn) {
                  <div class="step-inline-meta">
                    @if (step.completedBy) {
                      <span class="step-inline-meta__k">Completed by</span>
                      <span class="step-inline-meta__v">{{ step.completedBy }}</span>
                    }
                    @if (step.completedBy && step.completedOn) {
                      <span class="step-inline-meta__sep">·</span>
                    }
                    @if (step.completedOn) {
                      <span class="step-inline-meta__k">Completed At</span>
                      <span class="step-inline-meta__v">{{ step.completedOn | date: 'MMM d, yyyy, HH:mm:ss' : '+0100' }} WAT</span>
                    }
                  </div>
                }
              </div>
            }
            <div class="step-sub">{{ getCollapsedSummary(step) }}</div>
            @if (isStepOpen(step.number)) {
              <div
                [id]="'workflow-step-panel-' + step.number"
                [attr.aria-labelledby]="'workflow-step-trigger-' + step.number"
                class="step-body"
                role="region">
                @if (step.action) {
                  <dl class="step-meta">
                    <div class="step-meta-row">
                      <dt>Decision</dt>
                      <dd>{{ step.action === 'Approve' ? 'Approved' : 'Rejected' }}</dd>
                    </div>
                  </dl>
                }
                @if (step.details?.length) {
                  <ul class="step-details">
                    @for (detail of step.details; track detail) {
                      <li>{{ detail }}</li>
                    }
                  </ul>
                }
                @if (step.blocks; as blocks) {
                  @if (blocks.length) {
                    <div class="step-blocks">
                      <app-detail-summary-blocks [blocks]="blocks" />
                    </div>
                  }
                }
                @let evidenceBlocks = parseEvidenceBlocks(step.evidenceDataJson);
                @if (evidenceBlocks?.length) {
                  <div class="step-blocks">
                    <app-detail-summary-blocks [blocks]="evidenceBlocks" [wrapTables]="true" />
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
</section>
}
